import { cn } from '@/app/shared/lib/cn';
import { formatNumber } from '@/app/shared/lib/formatters';
import { PerformanceChartData } from '@/app/shared/ui/PerformanceChart/types';
import { useThemeStore } from '@/app/store/theme';

interface TooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

interface VaultsItemChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  metric?: PerformanceChartData;
}

const formatTooltipValue = (
  value: number | undefined,
  metric: PerformanceChartData,
  name?: string,
): string => {
  if (value == null || Number.isNaN(value)) return '-';
  if (name === 'Total TVL' || metric === PerformanceChartData.TVL) {
    return `$${formatNumber(value, 2, 2)}`;
  }
  if (name === 'Base APR' || metric === PerformanceChartData.APR) {
    return `${formatNumber(value, 2, 2)}%`;
  }
  if (metric === PerformanceChartData.SHARE_PRICE) return `$${formatNumber(value, 3, 2)}`;
  return `${formatNumber(value, 2, 2)}%`;
};

const VaultsItemChartTooltip = ({
  active,
  payload,
  label,
  metric = PerformanceChartData.APR,
}: VaultsItemChartTooltipProps) => {
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  if (!active || !payload?.length) return null;

  return (
    <div className='min-w-44 rounded-md bg-gray-900 px-4 py-3 shadow-xl'>
      <p className={cn('mb-2 text-sm font-medium', isLight ? 'text-brand-1100' : 'text-gray-400')}>{label}</p>
      <div className='flex flex-col gap-1'>
        {payload.map((entry) => (
          <div
            key={entry.name}
            className={cn(
              'flex items-center justify-between gap-4 text-sm font-medium',
              isLight ? 'text-brand-1100' : 'text-white',
            )}
          >
            <span className='flex items-center gap-2'>
              <span className='h-1.5 w-1.5 rounded-full' style={{ background: entry.color }} />
              {entry.name}
            </span>
            <span>{formatTooltipValue(entry.value, metric, entry.name)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VaultsItemChartTooltip;
